import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import SunEditor from 'suneditor-react';
import {
  blockquote,
  align,
  font,
  fontSize,
  fontColor,
  hiliteColor,
  horizontalRule,
  list,
  table,
  formatBlock,
  lineHeight,
  template,
  paragraphStyle,
  textStyle,
  link,
  image,
} from "suneditor/src/plugins";
import { SPANISH_ITEM_LOWER, SPANISH_ITEM_UPPER } from 'utils/config/reference';
import SentryWrap from 'components/Sentry';

const SPANISH_ITEM_LOWER_PLUGIN = {
  name: "SPANISH_ITEM_LOWER",
  display: "submenu",
  add: function (core, targetElement) {
    let listDiv = this.setSubmenu.call(core);
    var self = this;
    listDiv.querySelectorAll(".se-btn-list-lower").forEach(function (btn) {
      btn.addEventListener("click", self.onClick.bind(core));
    });
    core.initMenuTarget(this.name, targetElement, listDiv);
  },

  setSubmenu: function () {
    const listDiv = this.util.createElement("DIV");
    listDiv.className = "se-submenu se-list-layer";
    listDiv.innerHTML = `<div class="se-list-inner se-list-font-size" style="width:30px;">
      <ul class="se-list-basic">
         ${SPANISH_ITEM_LOWER.map(
           (item) =>
             `<li><button type="button" class="se-btn-list-lower" value="${item}">${item}</button></li>`
         ).join("")}
      </ul></div>`;

    return listDiv;
  },
  onClick: function (e) {
    e.preventDefault()
    const value = e.target.value;
    const node = this.util.createElement("span");
    this.util.addClass(node, "se-custom-tag");
    node.textContent = value;
    this.insertNode(node);
    const zeroWidthSpace = this.util.createTextNode(this.util.zeroWidthSpace);
    node.parentNode.insertBefore(zeroWidthSpace, node.nextSibling);
    // ✅ Dispatch proper input event so SunEditor calls onChange
      if (this.context?.element?.wysiwyg) {
        const event = new InputEvent("input", {
          bubbles: true,
          cancelable: true,
          inputType: "insertText",
          data: value,
        });
        this.context.element.wysiwyg.dispatchEvent(event);
      }


    this.submenuOff();
  },
};

const SPANISH_ITEM_UPPER_PLUGIN = {
  name: "SPANISH_ITEM_UPPER",
  display: "submenu",
  add: function (core, targetElement) {
    let listDiv = this.setSubmenu.call(core);
    var self = this;
    listDiv.querySelectorAll(".se-btn-list-upper").forEach(function (btn) {
      btn.addEventListener("click", self.onClick.bind(core));
    });
    core.initMenuTarget(this.name, targetElement, listDiv);
  },

  setSubmenu: function () {
    const listDiv = this.util.createElement("DIV");
    listDiv.className = "se-submenu se-list-layer";
    listDiv.innerHTML = `<div class="se-list-inner se-list-font-size" style="width:30px;">
      <ul class="se-list-basic">
         ${SPANISH_ITEM_UPPER.map(
           (item) =>
             `<li><button type="button" class="se-btn-list-upper" value="${item}">${item}</button></li>`
         ).join("")}
      </ul></div>`;

    return listDiv;
  },
  onClick: function (e) {
    const value = e.target.value;
    const node = this.util.createElement("span");
    this.util.addClass(node, "se-custom-tag");
    node.textContent = value;
    this.insertNode(node);
    const zeroWidthSpace = this.util.createTextNode(this.util.zeroWidthSpace);
    node.parentNode.insertBefore(zeroWidthSpace, node.nextSibling);
    // ✅ Dispatch proper input event so SunEditor calls onChange
    if (this.context?.element?.wysiwyg) {
      const event = new InputEvent("input", {
        bubbles: true,
        cancelable: true,
        inputType: "insertText",
        data: value,
      });
      this.context.element.wysiwyg.dispatchEvent(event);
    }
    this.submenuOff();
  },
};


const EmbeddedLessonPlanEditor = forwardRef(({
  lessonPlan
}, ref) => {
  const [editorContent, setEditorContent] = useState('');

  useEffect(() => {
    if (lessonPlan?.content) {
      setEditorContent(lessonPlan.content);
    }
  }, [lessonPlan?.content]);

  const handleContentChange = (content) => {
    setEditorContent(content);
  };

  // Expose the current editor content to parent component
  useImperativeHandle(ref, () => ({
    getCurrentContent: () => editorContent
  }));

  return (
    <SentryWrap>
      <Box style={{ 
      height: '100%',
      maxHeight: '80vh',
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '0px',
    }}>
      <Box className="editarModalLessonPlanSunEditor">
        <Box style={{ flex: 1, minHeight: 0, paddingBottom: '30px' }}>
          <Box style={{ borderBottom:"0.5px solid #80808094" , maxHeight: '75vh', overflowY: 'auto' , overflowX: 'hidden' }}>
            <SunEditor
          setContents={editorContent || ''}
          setDefaultStyle="text-align: left; height:100%; min-height:150px; padding-bottom:80px;"
          onChange={handleContentChange}
          setOptions={{
            height: 'calc(60vh - 100px)',
            plugins: [
              blockquote,
              align,
              font,
              fontSize,
              fontColor,
              hiliteColor,
              horizontalRule,
              list,
              table,
              formatBlock,
              lineHeight,
              template,
              paragraphStyle,
              textStyle,
              link,
              image,
              SPANISH_ITEM_LOWER_PLUGIN,
              SPANISH_ITEM_UPPER_PLUGIN,
            ],
            buttonList: [
              ['undo', 'redo'],
              ['font', 'fontSize', 'formatBlock'],
              ['paragraphStyle', 'blockquote'],
              ['bold', 'underline', 'italic'],
              ['fontColor', 'hiliteColor', 'textStyle'],
              ['removeFormat'],
              ['outdent', 'indent'],
              ['align', 'horizontalRule', 'list', 'lineHeight'],
              ['table'],
              [
                {
                  name: "SPANISH_ITEM_LOWER",
                  dataCommand: "SPANISH_ITEM_LOWER",
                  buttonClass: "",
                  title: `${SPANISH_ITEM_LOWER[0]}`,
                  dataDisplay: "submenu",
                  innerHTML: `<span>${SPANISH_ITEM_LOWER[0]}</span>`,
                },

                //...
              ],
              [
                {
                  name: "SPANISH_ITEM_UPPER",
                  dataCommand: "SPANISH_ITEM_UPPER",
                  buttonClass: "",
                  title: `${SPANISH_ITEM_UPPER[0]}`,
                  dataDisplay: "submenu",
                  innerHTML: `<span>${SPANISH_ITEM_UPPER[0]}</span>`,
                },
              ],
            ],
            formats: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
            placeholder: 'Edit your lesson plan content...'
          }}
        />
          </Box>
        </Box>
      </Box>
      </Box>
    </SentryWrap>
  );
});

export default EmbeddedLessonPlanEditor;
